import { normalizeExpiry, normalizeUrlPath } from "../campaign";
import type { AnswerOption, Campaign, ConfigVersion, ProblemTag, Question, Settings, Solution, SolutionAttributes } from "../types";

/** Raw tab contents keyed by tab name, each a 2D array with the header row first. */
export interface RawSheetTabs {
  BoardMembers: string[][];
  Questions: string[][];
  ProblemTags: string[][];
  AnswerTags: string[][];
  Settings: string[][];
  /** Optional: a Sheet without the tab yet reads as no campaigns (src/config/sync.ts). */
  Campaigns?: string[][];
}

/** The BoardMembers columns with dedicated Solution fields. Anything else on that tab is treated
 * as a SolutionAttribute automatically (see transformSolutions) — adding an attribute is a Sheet
 * column, full stop, no code change and no schema edit (the schema's solutionAttributes keeps
 * additionalProperties open for exactly this reason). */
const BOARD_MEMBER_FIXED_COLUMNS = new Set([
  "id",
  "name",
  "active",
  "primary_specialties",
  "secondary_specialties",
  "one_liner",
  "booking_url",
]);

export function rowsToObjects(tab: string[][]): Record<string, string>[] {
  const [header, ...rows] = tab;
  if (!header) return [];
  return rows
    .filter((row) => row.some((cell) => cell !== ""))
    .map((row) => {
      const obj: Record<string, string> = {};
      header.forEach((col, i) => {
        obj[col] = row[i] ?? "";
      });
      return obj;
    });
}

/** Every Sheet cell is a blank-defaulted string once through rowsToObjects; this just gives
 * TypeScript (with noUncheckedIndexedAccess) the same guarantee back at the call site. Trimmed:
 * a Sheet cell with incidental leading/trailing whitespace (e.g. a trailing space after a typed
 * name) was silently landing in Solution.id verbatim, so the model's structured JSON output —
 * which has no reason to echo whitespace it was never shown as meaningful — could never match
 * ADR-006's exact-string candidate-set check, turning every otherwise-successful ranking call
 * into a fallback_model_error. Trimming here, once, at the source, fixes ids/keys and is harmless
 * for free-text fields (question text, oneLiner, etc). */
export function field(row: Record<string, string>, key: string): string {
  return (row[key] ?? "").trim();
}

/** AnswerTags rows and Questions options are authored independently in the Sheet, so a copy/paste
 * artifact (extra spacing around a slash, a trailing semicolon left over from the options list)
 * makes two strings that read the same to a person fail an exact match and silently drop the row's
 * tag weights (matching-coverage-assessment 7.3/8.5, three occurrences across three repair passes).
 * Stripping a trailing separator and collapsing whitespace before joining into the lookup key
 * closes that class of mismatch rather than relying on the Sheet always being retyped perfectly. */
export function normalizeOptionKey(questionId: string, option: string): string {
  return `${questionId}::${option.replace(/;+$/, "").replace(/\s+/g, "")}`;
}

function parseBool(value: string): boolean {
  return value.trim().toUpperCase() === "TRUE";
}

function splitSemicolons(value: string): string[] {
  return value
    .split(";")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function transformProblemTags(tab: string[][]): ProblemTag[] {
  return rowsToObjects(tab).map((r) => ({
    tagId: field(r, "tag_id"),
    label: field(r, "label"),
    description: field(r, "description"),
  }));
}

function transformSolutions(tab: string[][]): Solution[] {
  const header = tab[0] ?? [];
  const attributeColumns = header.filter((col) => col && !BOARD_MEMBER_FIXED_COLUMNS.has(col));

  return rowsToObjects(tab).map((r) => {
    const attributes: SolutionAttributes = {};
    for (const col of attributeColumns) {
      const value = field(r, col);
      if (value) attributes[col] = value;
    }
    const solution: Solution = {
      id: field(r, "id"),
      name: field(r, "name"),
      active: parseBool(field(r, "active")),
      primarySpecialties: splitSemicolons(field(r, "primary_specialties")),
      secondarySpecialties: splitSemicolons(field(r, "secondary_specialties")),
      oneLiner: field(r, "one_liner"),
    };
    const bookingUrl = field(r, "booking_url");
    if (bookingUrl) solution.bookingUrl = bookingUrl;
    if (Object.keys(attributes).length > 0) solution.attributes = attributes;
    return solution;
  });
}

/** AnswerTags rows keyed by "questionId::optionLabel" -> tagWeights, so Questions transform can
 * look weights up per option without re-scanning the whole tab per option. */
function indexAnswerTags(tab: string[][]): Map<string, { tagId: string; weight: 1 | 2 | 3 }[]> {
  const index = new Map<string, { tagId: string; weight: 1 | 2 | 3 }[]>();
  for (const r of rowsToObjects(tab)) {
    const key = normalizeOptionKey(field(r, "question_id"), field(r, "option"));
    const weight = Number(field(r, "weight"));
    const list = index.get(key) ?? [];
    list.push({ tagId: field(r, "tag_id"), weight: weight as 1 | 2 | 3 });
    index.set(key, list);
  }
  return index;
}

function transformQuestions(tab: string[][], answerTagsIndex: Map<string, { tagId: string; weight: 1 | 2 | 3 }[]>): Question[] {
  return rowsToObjects(tab).map((r) => {
    const id = field(r, "id");
    const answerType = field(r, "answer_type") as Question["answerType"];
    // An options_from_attribute question's options are built per session from the candidates'
    // attribute values, never authored, so anything in its options cell is ignored here. Any
    // AnswerTags rows written against those ignored labels still surface as answer-tags-row-orphaned.
    const optionsFromAttribute = parseBool(field(r, "options_from_attribute"));
    const options: AnswerOption[] =
      answerType === "pii" || optionsFromAttribute
        ? []
        : splitSemicolons(field(r, "options")).map((label, i) => ({
            id: `${id}-opt-${i}`,
            label,
            order: i,
            tagWeights: answerTagsIndex.get(normalizeOptionKey(id, label)) ?? [],
          }));
    const question: Question = {
      id,
      order: Number(field(r, "order")),
      active: parseBool(field(r, "active")),
      text: field(r, "text"),
      answerType,
      allowsNone: parseBool(field(r, "allows_none")),
      excludeFromFiltering: parseBool(field(r, "exclude_from_filtering")),
      // ADR-011: opposite default from excludeFromFiltering/allowsNone above — a blank cell or a
      // missing pool_eligible column means true (selectable), since opting a question *out* of the
      // pool is the unusual case, not the norm every existing Sheet row needs to keep working.
      poolEligible: field(r, "pool_eligible").toUpperCase() !== "FALSE",
      options,
    };
    const pinnedPositionRaw = field(r, "pinned_position");
    if (pinnedPositionRaw) question.pinnedPosition = Number(pinnedPositionRaw);
    const askWhen = field(r, "ask_when");
    if (askWhen) question.askWhen = askWhen;
    const discriminatesAttribute = field(r, "discriminates_attribute");
    if (discriminatesAttribute) question.discriminatesAttribute = discriminatesAttribute;
    // Omitted when FALSE/blank rather than written as false, so adding the column leaves every
    // existing question's published shape (and the config checksum) unchanged.
    if (optionsFromAttribute) question.optionsFromAttribute = true;
    return question;
  });
}

function transformSettings(tab: string[][]): Settings {
  const kv = new Map(rowsToObjects(tab).map((r) => [field(r, "key"), field(r, "value")]));
  const num = (key: string): number => Number(kv.get(key) ?? "");
  const bool = (key: string): boolean => parseBool(kv.get(key) ?? "");
  const retentionRaw = kv.get("telemetryRetentionDays");
  const settings: Settings = {
    enabled: bool("enabled"),
    confidenceThreshold: num("confidence_threshold"),
    tagInclusionFloor: num("tagInclusionFloor"),
    requireEmailToReveal: bool("requireEmailToReveal"),
    model: kv.get("model") ?? "",
    monthlySpendCeilingUsd: num("monthly_spend_ceiling_usd"),
    sessionTtlHours: num("session_ttl_hours"),
    telemetryRetentionDays: !retentionRaw || retentionRaw === "indefinite" ? null : Number(retentionRaw),
    findABoardMemberUrl: kv.get("findABoardMemberUrl") ?? "",
    // ADR-011 Section 4. A missing row here becomes NaN, same as every other num() field above —
    // deliberately not defaulted here (that would fail *open*, the wrong direction for a cost
    // guard). validateBusinessRules' explicit NaN check is what actually refuses the publish.
    minQuestions: num("minQuestions"),
    maxQuestions: num("maxQuestions"),
    selectorModel: kv.get("selectorModel") ?? "",
    sessionStartsPerIpPerHour: num("sessionStartsPerIpPerHour"),
    reportViewsPerIpPerHour: num("reportViewsPerIpPerHour"),
    selectorTimeoutMs: num("selectorTimeoutMs"),
    rankingTimeoutMs: num("rankingTimeoutMs"),
    maxAlternates: num("maxAlternates"),
  };
  // Optional, same omit-when-blank treatment as Solution.bookingUrl above: no Sheet row yet means
  // no key in kv, and we'd rather leave the field off than ship an empty-string "url".
  const privacyPolicyUrl = kv.get("privacyPolicyUrl");
  if (privacyPolicyUrl) settings.privacyPolicyUrl = privacyPolicyUrl;
  return settings;
}

/** docs/plans/2026-10-13-campaigns.md. Paths and dates are normalized here so a full URL, a
 * trailing slash, or a cell Sheets stored as a date serial all publish the same way; whatever is
 * still malformed afterwards is left for validateBusinessRules to name. */
function transformCampaigns(tab: string[][]): Campaign[] {
  return rowsToObjects(tab).map((r) => ({
    id: field(r, "id"),
    urlPath: normalizeUrlPath(field(r, "url_path")),
    description: field(r, "description"),
    code: field(r, "code"),
    expires: normalizeExpiry(field(r, "expires")),
  }));
}

/** Produces the ConfigVersion payload minus versionId/publishedAt/sourceChecksum, which the sync
 * orchestrator fills in once it knows whether this content is actually new. */
export function transformSheetTabs(tabs: RawSheetTabs): Omit<ConfigVersion, "versionId" | "publishedAt" | "sourceChecksum"> {
  const answerTagsIndex = indexAnswerTags(tabs.AnswerTags);
  const campaigns = transformCampaigns(tabs.Campaigns ?? []);
  return {
    problemTags: transformProblemTags(tabs.ProblemTags),
    solutions: transformSolutions(tabs.BoardMembers),
    questions: transformQuestions(tabs.Questions, answerTagsIndex),
    settings: transformSettings(tabs.Settings),
    // Omitted when empty, so a Sheet with no Campaigns rows publishes the same shape as before.
    ...(campaigns.length > 0 ? { campaigns } : {}),
  };
}
